import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Checkbox } from '@/components/ui/checkbox';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Loader2, Edit, Trash2 } from 'lucide-react';
import { userService } from '../../_services';

function ManageProfile({
    wholesaler_name,
    wholesalerId,
    whPrefix,
    activePagin,
    whSearch,
    isDynamic,
    closeEditProfilePopup,
    openWholesalerPopup,
    dynamicCommission,
    advanceSetting,
    handleBonus,
    addOverrides,
    openAddOverridePopup
}) {
    const [loading, setLoading] = useState(false);
    const [btnLoader, setBtnLoader] = useState(false);
    const [error, setError] = useState('');
    const [successMessage, setSuccessMessage] = useState('');
    const [userList, setUserList] = useState([]);
    const [commissionMilestone, setCommissionMilestone] = useState([]);
    const [isCustomGroup, setIsCustomGroup] = useState(false);
    const [isEditable, setIsEditable] = useState(false);

    const userdata = useMemo(() => {
        try {
            return JSON.parse(localStorage.getItem('user'));
        } catch {
            return null;
        }
    }, []);

    const handleResponse = useCallback((response) => {
        return response.text().then(text => {
            try {
                const data = text && JSON.parse(text);
                return data;
            } catch (error) {
                return [];
            }
        });
    }, []);

    const fetchData = useCallback(async (roleName, roleType, id) => {
        setLoading(true);
        try {
            const requestOptions = {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ roleName, roleType, id })
            };
            const response = await fetch(`/api/admin/GetNewWhDetail`, requestOptions);
            const apidata = await handleResponse(response);

            if (apidata?.length > 0) {
                let processedData = [...apidata];
                
                if (isDynamic) {
                    processedData = processedData.map(item => ({
                        ...item,
                        isDynamic: true,
                        tierJson: item.tierJson ? JSON.parse(item.tierJson) : ''
                    }));
                } else {
                    processedData = processedData.map(item => ({
                        ...item,
                        tierJson: item.tierJson ? JSON.parse(item.tierJson) : ''
                    }));
                }

                setUserList(processedData);
                
                // Ensure commissionMilestone is always an array
                let milestones = [];
                try {
                    const msJson = processedData[0].msJson;
                    if (msJson) {
                        const parsed = JSON.parse(msJson);
                        milestones = Array.isArray(parsed) ? parsed : [];
                    }
                } catch (e) {

                    milestones = [];
                }
                setCommissionMilestone(milestones);
                
                setIsCustomGroup(processedData[0].isCustomGroup || false);
                setIsEditable(processedData[0].isDynamic || false);
            }
        } catch (error) {

        } finally {
            setLoading(false);
        }
    }, [isDynamic, handleResponse]);

    useEffect(() => {
        if (userdata && wholesalerId) {
            fetchData(userdata.paramsname, userdata.userrole, wholesalerId);
        }
    }, [userdata, wholesalerId, fetchData]);

    const addMilestone = useCallback(() => {
        const newMilestone = {
            msid: commissionMilestone.length + 1,
            from: '',
            to: '',
            commission: '',
            isineditedmode: true
        };
        setCommissionMilestone(prev => [...prev, newMilestone]);
        setError('');
    }, [commissionMilestone]);

    const handleEdit = useCallback((index) => {
        setCommissionMilestone(prev => {
            const updated = [...prev];
            updated[index] = { ...updated[index], isineditedmode: true };
            return updated;
        });
    }, []);

    const saveData = useCallback((index, from, to, commission) => {
        const wholeNumber = /^(?:0|(?:[1-9](?:\d{0,2}(?:,\d{3})+|\d*)))$/;

        if (!from || !to || !commission || !userService.validateCurrency(commission)) {
            setError("All fields are required and commission must be numeric e.g. 1.5");
            return;
        }

        if (!wholeNumber.test(from)) {
            setError('Invalid from value');
            return;
        }

        if (!wholeNumber.test(to)) {
            setError('Invalid to value');
            return;
        }

        if (parseFloat(commission) > 10) {
            setError('Commission should not be more than 10');
            return;
        }

        if (parseFloat(commission) <= 0) {
            setError('Commission should be greater than 0');
            return;
        }

        if (parseInt(to) <= parseInt(from)) {
            setError('The milestone To field should be greater than the From field');
            return;
        }

        setCommissionMilestone(prev => {
            const updated = [...prev];
            updated[index] = {
                ...updated[index],
                from,
                to,
                commission,
                isineditedmode: false
            };
            return updated;
        });
        setError('');
    }, []);

    const cancelList = useCallback((index) => {
        const milestone = commissionMilestone[index];
        
        if (!milestone.from && !milestone.to && !milestone.commission) {
            setCommissionMilestone(prev => {
                const updated = [...prev];
                updated.splice(index, 1);
                return updated;
            });
        } else {
            setCommissionMilestone(prev => {
                const updated = [...prev];
                updated[index] = { ...updated[index], isineditedmode: false };
                return updated;
            });
        }
    }, [commissionMilestone]);

    const deleteMilestone = useCallback((index) => {
        setCommissionMilestone(prev => {
            const updated = [...prev];
            updated.splice(index, 1);
            return updated;
        });
    }, []);

    const handleSaveSubmit = useCallback(async (e, item) => {
        e.preventDefault();
        setBtnLoader(true);
        setError('');

        // Check if any milestones are in edit mode
        const hasEditMode = Array.isArray(commissionMilestone) && commissionMilestone.some(m => m.isineditedmode);
        if (hasEditMode) {
            setError('Please save all milestone edits first');
            setBtnLoader(false);
            return;
        }

        try {
            const requestOptions = {
                method: 'POST',
                headers: { 
                    'Content-Type': 'application/json',
                    'MSJsonHeader': JSON.stringify(commissionMilestone)
                },
                body: JSON.stringify({
                    whid: wholesalerId,
                    roleName: userdata.paramsname,
                    roleType: userdata.userrole,
                    whname: wholesaler_name,
                    whemail: item.emailId,
                    whcontact: item.contactName,
                    whcomm: item.defaultCommission,
                    isruled: item.isRuleBased,
                    isGlicEnable: item.isGlicRx,
                    isdashboardneeded: true,
                    iscustomgroup: isCustomGroup,
                    gnprefix: whPrefix || '',
                    companyid: item.companyId,
                    isDynamic: item.isDynamic,
                    isTriprx: item.isTripRx,
                    isprescribeData: item.isPrescriberData
                })
            };

            const response = await fetch(`/api/admin/AddNewWhDetail`, requestOptions);
            const apidata = await handleResponse(response);

            if (apidata?.length > 0) {
                if (apidata[0].code === "1") {
                    setSuccessMessage(apidata[0].statusMessage || 'Profile updated successfully');
                    setError('');
                    setTimeout(() => {
                        closeEditProfilePopup(wholesaler_name, wholesalerId, whPrefix, activePagin, whSearch);
                        openWholesalerPopup(activePagin, whSearch);
                    }, 2000);
                } else {
                    setError(apidata[0].statusMessage || 'Error updating profile');
                    setSuccessMessage('');
                }
            } else {
                setError('Error updating profile. Please try again.');
                setSuccessMessage('');
            }
        } catch (error) {

            setError('Error updating profile. Please try again.');
            setSuccessMessage('');
        } finally {
            setBtnLoader(false);
        }
    }, [commissionMilestone, isCustomGroup, wholesalerId, wholesaler_name, whPrefix, userdata, handleResponse, closeEditProfilePopup, openWholesalerPopup, activePagin, whSearch]);

    const handleCancel = useCallback(() => {
        closeEditProfilePopup(wholesaler_name, wholesalerId, whPrefix, activePagin, whSearch);
        openWholesalerPopup(activePagin, whSearch);
    }, [closeEditProfilePopup, openWholesalerPopup, wholesaler_name, wholesalerId, whPrefix, activePagin, whSearch]);

    const handleDynamicCommission = useCallback((item) => {
        closeEditProfilePopup(wholesaler_name, wholesalerId, whPrefix, activePagin, whSearch);
        dynamicCommission(wholesaler_name, wholesalerId, whPrefix, activePagin, whSearch, '', '', userList);
    }, [closeEditProfilePopup, dynamicCommission, wholesaler_name, wholesalerId, whPrefix, activePagin, whSearch, userList]);

    const handleAdvanceSetting = useCallback((item) => {
        const email = item?.emailId || '';
        closeEditProfilePopup(wholesaler_name, wholesalerId, whPrefix, activePagin, whSearch);
        advanceSetting(wholesaler_name, wholesalerId, whPrefix, activePagin, whSearch, email, item.isGlicRx.toString());
    }, [closeEditProfilePopup, advanceSetting, wholesaler_name, wholesalerId, whPrefix, activePagin, whSearch]);

    const handleBonusProgram = useCallback(() => {
        closeEditProfilePopup(wholesaler_name, wholesalerId, whPrefix, activePagin, whSearch);
        handleBonus(wholesaler_name, wholesalerId, whPrefix, activePagin, whSearch);
    }, [closeEditProfilePopup, handleBonus, wholesaler_name, wholesalerId, whPrefix, activePagin, whSearch]);

    const handleAddOverrides = useCallback(() => {
        const email = userList[0]?.emailId || '';
        closeEditProfilePopup(wholesaler_name, wholesalerId, whPrefix, activePagin, whSearch);
        addOverrides(wholesaler_name, wholesalerId, whPrefix, activePagin, whSearch, email);
    }, [closeEditProfilePopup, addOverrides, wholesaler_name, wholesalerId, whPrefix, activePagin, whSearch, userList]);

    const handleChainList = useCallback((item) => {
        closeEditProfilePopup(wholesaler_name, wholesalerId, whPrefix, activePagin, whSearch, item.emailId);
        openAddOverridePopup(wholesaler_name, wholesalerId, whPrefix, activePagin, whSearch, item.emailId, wholesalerId);
    }, [closeEditProfilePopup, openAddOverridePopup, wholesaler_name, wholesalerId, whPrefix, activePagin, whSearch]);

    return (
        <Dialog open={true} onOpenChange={handleCancel}>
            <DialogContent className="max-w-6xl">
                <DialogHeader>
                    <DialogTitle>Manage Profiles & Rules: {wholesaler_name}</DialogTitle>
                </DialogHeader>

                {loading ? (
                    <div className="flex justify-center items-center py-8">
                        <Loader2 className="h-8 w-8 animate-spin text-primary" />
                    </div>
                ) : (
                    <div className="flex flex-col gap-4">
                        {userList.length > 0 && userList.map((item, index) => (
                            <form key={index} onSubmit={(e) => handleSaveSubmit(e, item)} className="space-y-6">
                                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                    <div className="space-y-2">
                                        <Label>Hubspot Company Id</Label>
                                        <Input type="text" value={item.companyId} disabled className="bg-muted" />
                                    </div>
                                    <div className="space-y-2">
                                        <Label>Contact Name</Label>
                                        <Input type="text" value={item.contactName} disabled className="bg-muted" />
                                    </div>
                                    <div className="space-y-2">
                                        <Label>Commission</Label>
                                        <Input
                                            type="text"
                                            defaultValue={item.defaultCommission}
                                            onChange={(e) => {
                                                const updated = [...userList];
                                                updated[index].defaultCommission = e.target.value;
                                                setUserList(updated);
                                            }}
                                        />
                                    </div>
                                </div>

                                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                                    <div className="space-y-2">
                                        <Label>Commission Rules</Label>
                                        <RadioGroup
                                            value={item.isRuleBased ? 'yes' : 'no'}
                                            onValueChange={(value) => {
                                                const updated = [...userList];
                                                updated[index].isRuleBased = value === 'yes';
                                                setUserList(updated);
                                            }}
                                            disabled
                                        >
                                            <div className="flex items-center space-x-2">
                                                <RadioGroupItem value="yes" id={`rules-yes-${index}`} />
                                                <Label htmlFor={`rules-yes-${index}`}>Yes</Label>
                                            </div>
                                            <div className="flex items-center space-x-2">
                                                <RadioGroupItem value="no" id={`rules-no-${index}`} />
                                                <Label htmlFor={`rules-no-${index}`}>No</Label>
                                            </div>
                                        </RadioGroup>
                                    </div>

                                    <div className="space-y-2">
                                        <Label>Enable GlicRx</Label>
                                        <div className="flex items-center space-x-2 mt-2">
                                            <Checkbox
                                                checked={item.isGlicRx}
                                                disabled
                                            />
                                            <Label>Enabled</Label>
                                        </div>
                                    </div>

                                    <div className="space-y-2">
                                        <Label>Custom Group Prefix?</Label>
                                        <RadioGroup
                                            value={isCustomGroup ? 'yes' : 'no'}
                                            onValueChange={(value) => setIsCustomGroup(value === 'yes')}
                                            disabled
                                        >
                                            <div className="flex items-center space-x-2">
                                                <RadioGroupItem value="yes" id={`custom-yes-${index}`} />
                                                <Label htmlFor={`custom-yes-${index}`}>Yes</Label>
                                            </div>
                                            <div className="flex items-center space-x-2">
                                                <RadioGroupItem value="no" id={`custom-no-${index}`} />
                                                <Label htmlFor={`custom-no-${index}`}>No</Label>
                                            </div>
                                        </RadioGroup>
                                    </div>

                                    <div className="space-y-2">
                                        <Label>Email</Label>
                                        <Input type="email" value={item.emailId} disabled className="bg-muted" />
                                    </div>
                                </div>

                                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                                    <div className="space-y-2">
                                        <Label>Dynamic User Commission</Label>
                                        <RadioGroup
                                            value={item.isDynamic ? 'yes' : 'no'}
                                            onValueChange={(value) => {
                                                const updated = [...userList];
                                                updated[index].isDynamic = value === 'yes';
                                                setUserList(updated);
                                            }}
                                        >
                                            <div className="flex items-center space-x-2">
                                                <RadioGroupItem value="yes" id={`dynamic-yes-${index}`} />
                                                <Label htmlFor={`dynamic-yes-${index}`}>Yes</Label>
                                            </div>
                                            <div className="flex items-center space-x-2">
                                                <RadioGroupItem value="no" id={`dynamic-no-${index}`} />
                                                <Label htmlFor={`dynamic-no-${index}`}>No</Label>
                                            </div>
                                        </RadioGroup>
                                    </div>

                                    <div className="space-y-2">
                                        <Label>Enable TripRx</Label>
                                        <div className="flex items-center space-x-2 mt-2">
                                            <Checkbox
                                                checked={item.isTripRx}
                                                onCheckedChange={(checked) => {
                                                    const updated = [...userList];
                                                    updated[index].isTripRx = checked;
                                                    setUserList(updated);
                                                }}
                                            />
                                            <Label>Enabled</Label>
                                        </div>
                                    </div>

                                    <div className="space-y-2">
                                        <Label>Show Prescriber Data</Label>
                                        <RadioGroup
                                            value={item.isPrescriberData ? 'yes' : 'no'}
                                            onValueChange={(value) => {
                                                const updated = [...userList];
                                                updated[index].isPrescriberData = value === 'yes';
                                                setUserList(updated);
                                            }}
                                        >
                                            <div className="flex items-center space-x-2">
                                                <RadioGroupItem value="yes" id={`prescriber-yes-${index}`} />
                                                <Label htmlFor={`prescriber-yes-${index}`}>Yes</Label>
                                            </div>
                                            <div className="flex items-center space-x-2">
                                                <RadioGroupItem value="no" id={`prescriber-no-${index}`} />
                                                <Label htmlFor={`prescriber-no-${index}`}>No</Label>
                                            </div>
                                        </RadioGroup>
                                    </div>
                                </div>

                                {item.isRuleBased && (
                                    <div className="border rounded-lg p-4 space-y-4">
                                        <div className="flex justify-between items-center">
                                            <h2 className="text-lg font-semibold">Commission Milestones</h2>
                                            <Button type="button" variant="secondary" onClick={addMilestone}>
                                                Add Milestone
                                            </Button>
                                        </div>

                                        {commissionMilestone.length > 0 ? (
                                            <div className="border rounded-lg overflow-hidden">
                                                <Table>
                                                    <TableHeader>
                                                        <TableRow>
                                                            <TableHead className="w-[80px]">Milestone</TableHead>
                                                            <TableHead>From</TableHead>
                                                            <TableHead>To</TableHead>
                                                            <TableHead>Commission</TableHead>
                                                            <TableHead className="w-[150px]">Actions</TableHead>
                                                        </TableRow>
                                                    </TableHeader>
                                                    <TableBody>
                                                        {commissionMilestone.map((milestone, idx) => (
                                                            <TableRow key={idx}>
                                                                <TableCell>{idx + 1}</TableCell>
                                                                <TableCell>
                                                                    {milestone.isineditedmode ? (
                                                                        <Input
                                                                            type="text"
                                                                            defaultValue={milestone.from}
                                                                            onChange={(e) => {
                                                                                const updated = [...commissionMilestone];
                                                                                updated[idx].from = e.target.value;
                                                                                setCommissionMilestone(updated);
                                                                            }}
                                                                        />
                                                                    ) : (
                                                                        milestone.from
                                                                    )}
                                                                </TableCell>
                                                                <TableCell>
                                                                    {milestone.isineditedmode ? (
                                                                        <Input
                                                                            type="text"
                                                                            defaultValue={milestone.to}
                                                                            onChange={(e) => {
                                                                                const updated = [...commissionMilestone];
                                                                                updated[idx].to = e.target.value;
                                                                                setCommissionMilestone(updated);
                                                                            }}
                                                                        />
                                                                    ) : (
                                                                        milestone.to
                                                                    )}
                                                                </TableCell>
                                                                <TableCell>
                                                                    {milestone.isineditedmode ? (
                                                                        <Input
                                                                            type="text"
                                                                            defaultValue={milestone.commission}
                                                                            onChange={(e) => {
                                                                                const updated = [...commissionMilestone];
                                                                                updated[idx].commission = e.target.value;
                                                                                setCommissionMilestone(updated);
                                                                            }}
                                                                        />
                                                                    ) : (
                                                                        milestone.commission
                                                                    )}
                                                                </TableCell>
                                                                <TableCell>
                                                                    {milestone.isineditedmode ? (
                                                                        <div className="flex gap-2">
                                                                            <Button
                                                                                type="button"
                                                                                size="sm"
                                                                                onClick={() => saveData(idx, milestone.from, milestone.to, milestone.commission)}
                                                                            >
                                                                                Save
                                                                            </Button>
                                                                            <Button
                                                                                type="button"
                                                                                size="sm"
                                                                                variant="secondary"
                                                                                onClick={() => cancelList(idx)}
                                                                            >
                                                                                Cancel
                                                                            </Button>
                                                                        </div>
                                                                    ) : (
                                                                        <div className="flex gap-2">
                                                                            <Button
                                                                                type="button"
                                                                                size="sm"
                                                                                variant="ghost"
                                                                                onClick={() => handleEdit(idx)}
                                                                            >
                                                                                <Edit className="h-4 w-4" />
                                                                            </Button>
                                                                            <Button
                                                                                type="button"
                                                                                size="sm"
                                                                                variant="ghost"
                                                                                onClick={() => deleteMilestone(idx)}
                                                                            >
                                                                                <Trash2 className="h-4 w-4 text-destructive" />
                                                                            </Button>
                                                                        </div>
                                                                    )}
                                                                </TableCell>
                                                            </TableRow>
                                                        ))}
                                                    </TableBody>
                                                </Table>
                                            </div>
                                        ) : (
                                            <div className="text-center py-4 text-muted-foreground">
                                                No milestones added yet
                                            </div>
                                        )}
                                    </div>
                                )}

                                {error && (
                                    <div className="text-sm text-destructive bg-destructive/10 p-3 rounded-md text-center">
                                        {error}
                                    </div>
                                )}

                                {successMessage && (
                                    <div className="text-sm text-green-700 bg-green-50 p-3 rounded-md border border-green-200 text-center">
                                        {successMessage}
                                    </div>
                                )}

                                <div className="flex flex-wrap gap-2 justify-between items-center">
                                    <div className="flex flex-wrap gap-2">
                                        {item.isDynamic && (
                                            <Button
                                                type="button"
                                                variant="secondary"
                                                onClick={() => handleDynamicCommission(item)}
                                            >
                                                {isEditable ? 'Edit/View Tier Commission' : 'Add Tier Commission'}
                                            </Button>
                                        )}
                                        <Button type="button" variant="secondary" onClick={handleBonusProgram}>
                                            Bonus Program
                                        </Button>
                                        <Button
                                            type="button"
                                            variant="secondary"
                                            onClick={() => handleAdvanceSetting(item)}
                                        >
                                            Advance Settings
                                        </Button>
                                        <Button type="button" variant="secondary" onClick={handleAddOverrides}>
                                            Overrides
                                        </Button>
                                        <Button
                                            type="button"
                                            variant="secondary"
                                            onClick={() => handleChainList(item)}
                                        >
                                            Chain List
                                        </Button>
                                    </div>
                                    <div className="flex gap-2">
                                        <Button type="submit" disabled={btnLoader}>
                                            {btnLoader && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                                            Save
                                        </Button>
                                        <Button type="button" variant="secondary" onClick={handleCancel}>
                                            Cancel
                                        </Button>
                                    </div>
                                </div>
                            </form>
                        ))}
                    </div>
                )}
            </DialogContent>
        </Dialog>
    );
}

export default ManageProfile;
